/**
 * TimeSlot Adapters - All functions that produce ClientTimeSlot
 * 
 * Transforms various input types (API DTOs, component data, event props) into ClientTimeSlot
 */

import moment from "moment";
import { TimeSlotDto, AppointmentDto, PatientDto, EventTimeSlotDto, LeaveOfAbsenceDto } from "../../../../../api-client";
import { ClientTimeSlot, VisitType } from "../types";
import { adaptUserFromApi } from "./user.adapter";
import { adaptPatientFromApi } from "./patient.adapter";
import { adaptTherapyFromApi } from "./therapy.adapter";
import { slotTherapy } from "../../../../../helpers/slotTherapy";

/**
 * Transform API TimeSlotDto to simplified ClientTimeSlot
 */
export const adaptTimeSlotFromApi = (timeSlot: TimeSlotDto, isContinuous:boolean, visitType?: VisitType): ClientTimeSlot => {
  return {
    id: timeSlot.id!,
    start: new Date(timeSlot.start),
    end: new Date(timeSlot.end),
    type: (timeSlot.type as any) || 'TimeSlot',
    
    user: timeSlot.user ? adaptUserFromApi(timeSlot.user) : undefined,
    patient: timeSlot.patient ? adaptPatientFromApi(timeSlot.patient) 
      : timeSlot.therapyRx?.rx?.patient ? adaptPatientFromApi(timeSlot.therapyRx.rx.patient) 
      : undefined,
    therapy: slotTherapy(timeSlot)
      ? adaptTherapyFromApi(slotTherapy(timeSlot)!)
      : undefined,
    therapyRxId: timeSlot.therapyRx?.id,
    rx: timeSlot.therapyRx?.rx ? {
      id: timeSlot.therapyRx.rx.id!,
      date: timeSlot.therapyRx.rx.date ? timeSlot.therapyRx.rx.date : undefined,
      rxNumber: timeSlot.therapyRx.rx.rxNumber,
      isPerpetual: timeSlot.therapyRx.rx.isPerpetual || false
    } : undefined,

    appointmentId: timeSlot.appointmentId,
    treatmentIndex: timeSlot.treatmentIndex,
    attended: timeSlot.attended,
    wasDocumentedCount: timeSlot?.wasDocumentedCount,
    informing: timeSlot.informing,
    
    isCancelled: false, // TODO: determine from timeSlot data
    hasNotes: !!timeSlot.treatmentNote,

    visitType: visitType,
    visitFee: timeSlot.visitFee,
    isContinuous: isContinuous,
    groupAppointmentId: timeSlot?.groupAppointmentId,
  };
};

/**
 * Convert appointment with patient data to ClientTimeSlot for UI interactions
 * (e.g., for cancellation drawer clicks)
 */
export const adaptAppointmentToClientTimeSlot = (
  appointment: AppointmentDto,
  patient?: PatientDto
): ClientTimeSlot => {
  const firstTimeSlot = appointment.timeSlots?.[0];
  if (!firstTimeSlot) {
    throw new Error("Appointment must have at least one time slot");
  }

  // Use provided patient or extract from appointment
  const patientData = patient || (firstTimeSlot.therapyRx?.rx?.patient as PatientDto);
  
  return {
    id: appointment.id!,
    start: new Date(firstTimeSlot.start),
    end: new Date(firstTimeSlot.end),
    type: (firstTimeSlot.type as any) || 'TimeSlot',
    appointmentId: appointment.id,
    
    patient: patientData ? adaptPatientFromApi(patientData) : undefined,
    therapy: slotTherapy(firstTimeSlot)
      ? adaptTherapyFromApi(slotTherapy(firstTimeSlot)!)
      : undefined,
    
    attended: firstTimeSlot.attended,
    isCancelled: false, // TODO: determine cancellation status from appointment data
    hasNotes: !!firstTimeSlot.treatmentNote,
    isContinuous: false,
  };
};



/**
 * Extract ClientTimeSlot from FullCalendar event extendedProps
 * 
 *  This is used just prior to creating EventConntent
 * 
 * (e.g., for event click handlers)
 */
export const adaptEventPropsToClientTimeSlotDISABLED = (extendedProps: any, title: string): ClientTimeSlot => {
  // console.debug("Adapting event extendedProps to ClientTimeSlot:", extendedProps);
  
  return {
    id: extendedProps.timeSlotId || extendedProps.id,
    start: extendedProps.start ,
    end: extendedProps.end ,
    type: extendedProps.timeSlotType || 'TimeSlot',
    appointmentId: extendedProps.appointmentId,
    
    user: extendedProps.user,
    patient: extendedProps.patient,
    therapy: extendedProps.therapy,
    
    attended: extendedProps.attended,

    rx: extendedProps.rx,

    sublocality: extendedProps.sublocality,
    isContinuous: extendedProps.isContinuous,

    title: title,
  };
};



/**
 * Adapt EventTimeSlotDto to ClientTimeSlot
 */
export const adaptEventTimeSlotFromApi = (eventTimeSlot: EventTimeSlotDto, isContinuous: boolean): ClientTimeSlot => {
  return {
    id: eventTimeSlot.id!,
    start: new Date(eventTimeSlot.start!),
    end: new Date(eventTimeSlot.end!),
    type: 'EventTimeSlot',
    user: eventTimeSlot.user ? adaptUserFromApi(eventTimeSlot.user) : undefined,
    title: eventTimeSlot.title,
    isCancelled: false,
    hasNotes: false,
    isContinuous: isContinuous
  };
};

/**
 * Adapt LeaveOfAbsenceDto to ClientTimeSlot
 */
export const adaptLeaveOfAbsenceFromApi = (loa: LeaveOfAbsenceDto): ClientTimeSlot => {
  return {
    id: loa.id!,
    start: new Date(loa.start!),
    end: new Date(loa.end!),
    type: 'LeaveOfAbsence',
    user: loa.user ? adaptUserFromApi(loa.user) : undefined,
    title: `${loa.loAType || ''}`,
    loaType: loa.loAType,
    isCancelled: false,
    hasNotes: false,
    isContinuous: false
  };
};
